import { useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { textEntryIntentProps } from '@messenger/utils'
import Icon from 'components/priority/Icon/Icon'
import {
  caseColumnsWithTabularRows,
  editorTextBoxTableLabel,
  tabularCaseCells,
  tabularRowForm,
  tabularRowsFromCase,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorSelection,
  EditorTextBoxMount,
  TabularRow,
  TabularRowForm,
  TabularRowInput,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import {
  AUTHORING_PREVIEW_DEFAULT_STYLE,
  AuthoringFooter,
  AuthoringModalFrame,
  AuthoringPreviewCard,
  seedFieldRun,
  useAuthoringCommit,
} from './EditorTextBoxAuthoringModal'
import type { FieldAddress } from './EditorTextBoxAuthoringModal'
import * as S from './EditorTextBoxModal.styled'

type AuthorRows = (
  fieldKey: string,
  rows: readonly TabularRow[],
  rowType?: string,
) => Promise<boolean>

type TabularFieldModalProps = {
  getMount: () => EditorTextBoxMount | null
  getHostEl: () => HTMLDivElement | null
  /** The case as the app holds it, rows included — the flat field values carry no row. */
  caseRecord: Record<string, unknown>
  savedSel?: EditorSelection | null
  fieldKey: string
  /** The additional-family group whose rows the modal edits; unset, every row of the field. */
  rowType?: string | null
  /** The table the modal was opened on; unset, the box's first table of the group `rowType` names, else of the key. */
  field?: FieldAddress | null
  onAuthorRows?: AuthorRows
  onClose: () => void
}

/** One row under edit: a key React tracks it by, and the row as the case form holds it. */
type DraftRow = { key: number; row: TabularRow }

const inputText = (row: TabularRow, field: string): string =>
  typeof row[field] === 'string' ? (row[field] as string) : ''

/**
 * The row card nearest the pointer in the grip's list, and whether the
 * pointer is in its top half, where a released row lands before it.
 */
const rowUnderPointer = (
  handle: HTMLElement,
  clientY: number,
): { over: number; before: boolean } | null => {
  const list = handle.closest<HTMLElement>('[data-editor-text-table-rows]')
  if (!list) return null
  let nearest: { over: number; before: boolean; distance: number } | null = null
  for (const card of Array.from(
    list.querySelectorAll<HTMLElement>('[data-row-key]'),
  )) {
    const rect = card.getBoundingClientRect()
    const distance =
      clientY < rect.top
        ? rect.top - clientY
        : clientY > rect.bottom
          ? clientY - rect.bottom
          : 0
    if (nearest === null || distance < nearest.distance)
      nearest = {
        over: Number(card.getAttribute('data-row-key')),
        before: clientY < rect.top + rect.height / 2,
        distance,
      }
  }
  return nearest && { over: nearest.over, before: nearest.before }
}

/** The first cell of the box's first table of the field that names the group; null when the box holds none. */
const groupTableRun = (
  mount: EditorTextBoxMount | null,
  fieldKey: string,
  rowType: string,
): FieldAddress | null => {
  const blocks = mount?.document?.().box.blocks ?? []
  for (const [blockIndex, block] of blocks.entries()) {
    if (block.kind !== 'case_detail_table') continue
    const run = mount?.fieldRunAt?.(blockIndex, 0)
    if (run?.fieldKey === fieldKey && run.params?.rowType === rowType)
      return { blockIndex, runIndex: 0 }
  }
  return null
}

const TabularFieldModal = ({
  getMount,
  getHostEl,
  caseRecord,
  savedSel = null,
  fieldKey,
  rowType = null,
  field = null,
  onAuthorRows,
  onClose,
}: TabularFieldModalProps) => {
  const { t } = useTranslation()
  const form = tabularRowForm(fieldKey) as TabularRowForm
  const group = rowType || undefined
  const label = editorTextBoxTableLabel(t, fieldKey, group)
  const rowLabel = String(
    t(`caseFormSectionsLabels.${form.rowLabelKey}`, {
      defaultValue: form.rowLabel,
    }),
  )
  const inputLabel = (input: TabularRowInput): string =>
    String(
      t(`caseFormSectionsLabels.${input.field}`, {
        defaultValue: input.field,
      }),
    )
  const canAuthor = typeof onAuthorRows === 'function'

  const nextKey = useRef(0)
  const draftRow = (row: TabularRow): DraftRow => {
    nextKey.current += 1
    return { key: nextKey.current, row }
  }
  const [seedRows] = useState<TabularRow[]>(() =>
    tabularRowsFromCase(form, caseRecord, group),
  )
  const [rows, setRows] = useState<DraftRow[]>(() => seedRows.map(draftRow))

  const [seed] = useState<FieldAddress | null>(() => {
    const mount = getMount()
    if (group !== undefined && !field)
      return groupTableRun(mount, fieldKey, group)
    const run = seedFieldRun(mount, fieldKey, field)
    if (!run || (run.params?.rowType || undefined) !== group) return null
    return { blockIndex: run.blockIndex, runIndex: run.runIndex }
  })
  const editMode = seed !== null
  const { saving, error, run } = useAuthoringCommit()

  const draftRows = useMemo(() => rows.map((draft) => draft.row), [rows])
  const dirty = JSON.stringify(draftRows) !== JSON.stringify(seedRows)
  const previewCells = useMemo(
    () =>
      tabularCaseCells(
        fieldKey,
        {
          ...caseRecord,
          ...caseColumnsWithTabularRows(form, caseRecord, draftRows, group),
        },
        group,
      ),
    [fieldKey, form, caseRecord, draftRows, group],
  )

  const setRowValue = (key: number, fieldName: string, value: unknown) =>
    setRows((prev) =>
      prev.map((draft) =>
        draft.key === key
          ? { ...draft, row: { ...draft.row, [fieldName]: value } }
          : draft,
      ),
    )
  const addRow = () =>
    setRows((prev) => [...prev, draftRow(form.newRow(group))])
  const removeRow = (key: number) =>
    setRows((prev) => prev.filter((draft) => draft.key !== key))

  // Pallbearer rows reorder by their grips: the order the modal saves is the
  // order the case prints them in. Pointer events, as the name modal's parts
  // use, so a touch screen drags the way a mouse does; a focused grip moves
  // its row with the arrow keys.
  const reorderable = canAuthor && form.column === 'pallbearers'
  const [drag, setDrag] = useState<{
    key: number
    over: number | null
    before: boolean
  } | null>(null)

  const placeRow = (key: number, target: number, before: boolean) =>
    setRows((prev) => {
      const moving = prev.find((draft) => draft.key === key)
      const without = prev.filter((draft) => draft.key !== key)
      const at = without.findIndex((draft) => draft.key === target)
      if (!moving || at < 0) return prev
      const next = [...without]
      next.splice(before ? at : at + 1, 0, moving)
      return next.every((draft, i) => draft === prev[i]) ? prev : next
    })

  const moveRow = (key: number, step: -1 | 1) =>
    setRows((prev) => {
      const from = prev.findIndex((draft) => draft.key === key)
      const to = from + step
      if (from < 0 || to < 0 || to >= prev.length) return prev
      const next = [...prev]
      next.splice(to, 0, ...next.splice(from, 1))
      return next
    })

  const onGripPointerDown = (key: number) => (e: PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    setDrag({ key, over: null, before: true })
  }

  const onGripPointerMove = (e: PointerEvent<HTMLElement>) => {
    if (!drag || !e.currentTarget.hasPointerCapture(e.pointerId)) return
    const hit = rowUnderPointer(e.currentTarget, e.clientY)
    if (hit && (drag.over !== hit.over || drag.before !== hit.before))
      setDrag({ ...drag, ...hit })
  }

  const onGripPointerUp = (e: PointerEvent<HTMLElement>) => {
    if (!drag) return
    if (e.currentTarget.hasPointerCapture(e.pointerId))
      e.currentTarget.releasePointerCapture(e.pointerId)
    const hit = rowUnderPointer(e.currentTarget, e.clientY)
    if (hit) placeRow(drag.key, hit.over, hit.before)
    setDrag(null)
  }

  const onGripKeyDown = (key: number) => (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    moveRow(key, e.key === 'ArrowUp' ? -1 : 1)
  }

  const restoreCaret = () => {
    getHostEl()?.focus()
    getMount()?.placeCaretAtPos(savedSel?.focus ?? { blockIndex: 0, offset: 0 })
  }
  const close = () => {
    restoreCaret()
    onClose()
  }

  const commitRows = async (): Promise<boolean> => {
    if (!canAuthor || !dirty) return true
    return run(
      () => onAuthorRows!(fieldKey, draftRows, group),
      t('editor.textBox.modals.table.saveError'),
    )
  }

  const insertTable = async () => {
    if (!(await commitRows())) return
    getHostEl()?.focus()
    if (savedSel) getMount()?.placeRangeAtPos(savedSel.anchor, savedSel.focus)
    getMount()?.insertCaseDetailTable(
      fieldKey,
      group === undefined ? undefined : { rowType: group },
    )
    onClose()
  }

  const saveRows = async () => {
    if (!(await commitRows())) return
    close()
  }

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      if (editMode) saveRows()
      else insertTable()
    }
  }

  return (
    <AuthoringModalFrame
      backdropTestId="data-editor-text-table-modal-backdrop"
      onDismiss={close}
      dialogAttrs={{
        'data-editor-text-table-modal': true,
        'data-table-field': fieldKey,
        ...(group === undefined ? {} : { 'data-table-row-type': group }),
      }}
      ariaLabel={label}
      title={label}
      subtitle={
        editMode
          ? t('editor.textBox.modals.table.subtitleEdit')
          : t('editor.textBox.modals.table.subtitleInsert')
      }
      closeAttrs={{ 'data-editor-text-table-close': true }}
      footer={
        <AuthoringFooter
          cancelAttrs={{ 'data-editor-text-table-cancel': true }}
          onCancel={close}
          secondary={
            editMode || !canAuthor
              ? null
              : {
                  attrs: { 'data-editor-text-table-save-only': true },
                  label: t('editor.textBox.modals.common.saveToCase'),
                  title: dirty
                    ? t('editor.textBox.modals.table.saveOnlyTitle')
                    : t('editor.textBox.modals.table.unchangedTitle'),
                  disabled: !dirty,
                  onClick: saveRows,
                }
          }
          primaryAttrs={
            editMode
              ? { 'data-editor-text-table-save': true }
              : { 'data-editor-text-table-insert': true }
          }
          primaryLabel={
            editMode
              ? t('editor.textBox.modals.common.saveChanges')
              : t('editor.textBox.modals.table.insertTable')
          }
          onPrimary={editMode ? saveRows : insertTable}
          saving={saving}
        />
      }
    >
      <S.SplitBody>
        <S.SplitValueCol>
          <S.SectionLabel>
            {t('editor.textBox.modals.common.rows')}
          </S.SectionLabel>
          {!canAuthor ? (
            <S.Hint data-editor-text-table-readonly>
              These rows fill from the case, so they are not editable here —
              update them in case details.
            </S.Hint>
          ) : (
            <>
              <S.RowList data-editor-text-table-rows>
                {rows.map((draft, index) => (
                  <S.RowCard
                    key={draft.key}
                    data-editor-text-table-row
                    data-row-key={draft.key}
                    $dragging={drag?.key === draft.key}
                    $dropSide={
                      drag && drag.over === draft.key && drag.key !== draft.key
                        ? drag.before
                          ? 'before'
                          : 'after'
                        : null
                    }
                  >
                    {reorderable && (
                      <S.RowDragHandle
                        tabIndex={0}
                        role="button"
                        data-editor-text-table-drag
                        aria-label={t(
                          'editor.textBox.modals.table.dragRowAria',
                          {
                            rowLabelLower: rowLabel.toLowerCase(),
                          },
                        )}
                        title={t('editor.textBox.modals.table.dragRowTitle', {
                          rowLabelLower: rowLabel.toLowerCase(),
                        })}
                        onPointerDown={onGripPointerDown(draft.key)}
                        onPointerMove={onGripPointerMove}
                        onPointerUp={onGripPointerUp}
                        onPointerCancel={() => setDrag(null)}
                        onKeyDown={onGripKeyDown(draft.key)}
                      >
                        <Icon icon="bars2Icon" width="14px" height="14px" />
                      </S.RowDragHandle>
                    )}
                    <S.RowFields>
                      {form.inputs.map((input) =>
                        input.type === 'check' ? (
                          <S.RowCheck key={input.field}>
                            <S.PartToggle
                              type="checkbox"
                              data-table-value={input.field}
                              checked={draft.row[input.field] === true}
                              onChange={(e) =>
                                setRowValue(
                                  draft.key,
                                  input.field,
                                  e.target.checked,
                                )
                              }
                            />
                            {inputLabel(input)}
                          </S.RowCheck>
                        ) : (
                          <S.CompositePart key={input.field}>
                            <S.CompositePartLabel>
                              {form.plainStrings
                                ? `${rowLabel} ${index + 1}`
                                : inputLabel(input)}
                            </S.CompositePartLabel>
                            <S.ValueInput
                              data-table-value={input.field}
                              {...textEntryIntentProps()}
                              type="text"
                              value={inputText(draft.row, input.field)}
                              placeholder="—"
                              onChange={(e) =>
                                setRowValue(
                                  draft.key,
                                  input.field,
                                  e.target.value,
                                )
                              }
                              onKeyDown={onInputKeyDown}
                            />
                          </S.CompositePart>
                        ),
                      )}
                    </S.RowFields>
                    <S.RowRemoveBtn
                      type="button"
                      data-editor-text-table-remove
                      title={t('editor.textBox.modals.table.removeRow', {
                        rowLabel,
                        rowLabelLower: rowLabel.toLowerCase(),
                      })}
                      onClick={() => removeRow(draft.key)}
                    >
                      <Icon
                        icon="deleteOutline"
                        fill="gray2"
                        hoverfill="gray1"
                        width="16px"
                        height="16px"
                      />
                    </S.RowRemoveBtn>
                  </S.RowCard>
                ))}
              </S.RowList>
              <S.AddRowBtn
                type="button"
                data-editor-text-table-add
                onClick={addRow}
              >
                {t('nestedObjectWrapper.add', { label: rowLabel })}
              </S.AddRowBtn>
            </>
          )}
        </S.SplitValueCol>
        <S.SplitSideCol>
          <S.SectionLabel>
            {t('editor.textBox.modals.common.preview')}
          </S.SectionLabel>
          <AuthoringPreviewCard
            effective={AUTHORING_PREVIEW_DEFAULT_STYLE}
            text={previewCells.join('\n')}
            ghost={t('editor.textBox.modals.common.ghost', {
              label,
              labelLower: label.toLowerCase(),
            })}
            cardAttrs={{ 'data-editor-text-table-preview': true }}
            textAttrs={{ 'data-editor-text-table-preview-text': true }}
            ghostAttrs={{ 'data-editor-text-table-preview-ghost': true }}
            cardProps={{ $block: true }}
            textProps={{ $block: true }}
          />
          <S.Hint>
            {canAuthor
              ? t('editor.textBox.modals.table.hintEditable')
              : t('editor.textBox.modals.table.hintReadOnly')}
          </S.Hint>
          {error && (
            <S.ErrorText data-editor-text-table-error>{error}</S.ErrorText>
          )}
        </S.SplitSideCol>
      </S.SplitBody>
    </AuthoringModalFrame>
  )
}

export default TabularFieldModal
